import { useState, Fragment } from "react";
import { Button, Modal, Table, MediaQuery } from "@mantine/core";

import { Activity } from "@captiva/shared-types";
import { bulkGenerateAndDownloadQR } from "../../components/shared/bulk-qr-generator";
import { ActivityAssetConfig } from "../../components/listings/configurations/assets/activity-asset-config";
import { AssetEditor } from "../../components/shared/asset-editor";
import { FlexOptionsChoiceModal } from "./shared/flex-options-choice-modal";
import { Plus } from "tabler-icons-react";
import { useCreateFlexOptionsSet } from "../../hooks/useCreateFlexOptionsSet";
import TableRowActions from "./shared/table-row-actions";
import { BulkActionsMenu } from "./shared/bulk-actions-menu";

export interface AuthUser {
  uid: string;
  email?: string;
  claims?: {
    companyId?: string;
  };
}

interface ActivitiesTableProps {
  elements: Activity[];
  auth: AuthUser | null;
  reloadCallback: () => void;
}

export function ActivitiesTable({ elements, auth, reloadCallback }: ActivitiesTableProps) {
  const newActivity: Partial<Activity> = {
    name: "",
    rebateEligible: true,
  };
  const [editedElement, setEditedElement] = useState<Activity | Partial<Activity> | undefined>(undefined);
  const closeEdit = () => setEditedElement(undefined);
  const [createFlexOptionsSet, { loading, notification, clearNotification, existingDocs, mergeOptions, createNewSet }] = useCreateFlexOptionsSet();
  
  const handleCreateFlexOptionsSet = () => {
    const companyId = auth?.claims?.companyId;
    if (!companyId) {
      alert("No company ID found in auth claims.");
      return;
    }
    createFlexOptionsSet({
      assetType: "Activities",
      assetList: elements,
      metadataPrefix: "activity",

      fieldMappings: {
        label: "name",
        value: "id",
        metadata: {
          rebateEligible: "rebateEligible",
          name: "name",
        }
      },
      companyId
    });
  };

  const rows = elements.map((element) => (
    <Fragment key={element.id}>
      {/* // desktop sizes */}
      <MediaQuery smallerThan="sm" styles={{ display: "none" }}>
        <tr>
          <td>{element.name}</td>
          <td>{String(element.rebateEligible ?? true)}</td>
          <td>
            <TableRowActions
              element={element}
              setEditedElement={setEditedElement}
              reloadCallback={reloadCallback}
              assetName="Activity"
              collectionName="activities"
              nameKey="name"
              idKey="id"
            />
          </td>
        </tr>
      </MediaQuery>
      {/* // phone sizes */}
      <MediaQuery largerThan="sm" styles={{ display: "none" }}>
        <tr>
          <td>{element.name}</td>
          <td>
            <TableRowActions
              element={element}
              setEditedElement={setEditedElement}
              reloadCallback={reloadCallback}
              assetName="Activity"
              collectionName="activities"
              nameKey="name"
              idKey="id"
            />
          </td>
        </tr>
      </MediaQuery>
    </Fragment>
  ));

  async function handleBulkQRDownload() {
    await bulkGenerateAndDownloadQR({
      assetName: "Activity",
      items: elements,
      nameKey: "name",
      idKey: "id",
      zipName: "activities-qr-codes.zip",
      logoSrc: "/captiva-mark.png"
    });
  }
  return (
    <>
      {notification && notification.type !== "choice" && (
        <div style={{ position: "fixed", top: 76, right: 20, zIndex: 9999 }}>
          <Button color={notification.type === "success" ? "green" : "red"} variant="light" onClick={clearNotification}>
            {notification.message}
          </Button>
        </div>
      )}
      {elements.length > 0 && (
        <Table width="100%">
          <thead>
            <tr>
              <th>Name</th>
              <th>Rebate eligible</th>
              <th style={{textAlign: 'left'}}>
                <BulkActionsMenu onBulkQRDownload={handleBulkQRDownload} onCreateFlexOptionsSet={handleCreateFlexOptionsSet} loading={loading} />
              </th>
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </Table>
      )}
      {editedElement && (
        <Modal opened onClose={closeEdit} title="Activity">
          <AssetEditor
            asset={editedElement}
            assetConfig={ActivityAssetConfig}
            collection="activities"
            auth={auth}
            reloadCallback={() => {
              reloadCallback();
              closeEdit();
            }}
          ></AssetEditor>
        </Modal>
      )}
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Button
          onClick={() => setEditedElement(newActivity)}
          fullWidth
          style={{ maxWidth: 500 }}
          my="xl"
          color="cap-navy"
          variant="outline"
          leftIcon={<Plus />}
        >
          Create new
        </Button>
      </div>
      {notification && notification.type === "choice" && (
        <FlexOptionsChoiceModal
          opened={true}
          onClose={() => clearNotification()}
          onMerge={mergeOptions}
          onCreateNew={createNewSet}
          existingDocs={existingDocs}
          assetType="Activities"
        />
      )}
    </>
  );
}